import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { BRAND, navLinks } from "@/lib/brand";
import { trackPhoneCallLead } from "@/lib/ads";
import Logo from "./Logo";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const onHome = location.pathname === "/";

  useEffect(() => {
    let ticking = false;
    const update = () => {
      setScrolled(window.scrollY > 40);
      ticking = false;
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        document.getElementById("mobile-menu-toggle")?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <header className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${scrolled ? "bg-background/90 backdrop-blur-md border-b border-border" : "bg-transparent"}`}>
      <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-primary-foreground">Skip to content</a>
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 lg:px-8">
        <Logo />
        <nav className="hidden items-center gap-7 lg:flex">
          {navLinks.map((l) => {
            const useRoute = l.route && !onHome;
            return useRoute ? (
              <Link key={l.route} to={l.route} className="rounded text-[13px] font-medium text-muted-foreground transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">{l.label}</Link>
            ) : (
              <a key={l.href} href={l.href} className="rounded text-[13px] font-medium text-muted-foreground transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">{l.label}</a>
            );
          })}
        </nav>
        <div className="hidden items-center gap-3 lg:flex">
          <Button asChild variant="outline" size="sm" className="h-9">
            <a href={`tel:${BRAND.phoneTel}`} onClick={trackPhoneCallLead}>{BRAND.phoneDisplay}</a>
          </Button>
          <Button asChild size="sm" className="h-9 bg-accent text-white hover:bg-accent/90">
            <a href="#booking">Book Treatment</a>
          </Button>
        </div>
        <button id="mobile-menu-toggle" className="lg:hidden rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" onClick={() => setOpen(!open)} aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open} aria-controls="mobile-menu">
          {open ? <X className="h-6 w-6 text-primary" /> : <Menu className="h-6 w-6 text-primary" />}
        </button>
      </div>
      {open && (
        <div id="mobile-menu" className="border-t border-border bg-background px-5 py-4 lg:hidden">
          <div className="flex flex-col gap-3">
            {navLinks.map((l) => {
              const useRoute = l.route && !onHome;
              return useRoute ? (
                <Link key={l.route} to={l.route} onClick={() => setOpen(false)} className="rounded text-sm font-medium text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">{l.label}</Link>
              ) : (
                <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="rounded text-sm font-medium text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">{l.label}</a>
              );
            })}
            <Button asChild className="mt-2 h-11 bg-accent text-white hover:bg-accent/90">
              <a href="#booking" onClick={() => setOpen(false)}>Book Treatment</a>
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}